import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import CustomCheckbox from "./CustomCheckBox";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import Autocomplete from "components/AutoComplete";

const Step2 = ({ formData, setFormData, errors, setErrors }) => {
  const [isPartOfCompany, setIsPartOfCompany] = useState(formData.partOfCompany || false);
  const [isPersonalQuestionnaire, setIsPersonalQuestionnaire] = useState(formData.personalQuestionnaire || false);
  const [companies, setCompanies] = useState([]);
  const [referents, setReferents] = useState([]);

  // Synchroniser les états locaux avec formData au montage
  useEffect(() => {
    setIsPartOfCompany(formData.partOfCompany || false);
    setIsPersonalQuestionnaire(formData.personalQuestionnaire || false);
  }, [formData.partOfCompany, formData.personalQuestionnaire]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const companiesSnapshot = await getDocs(collection(db, "companies"));
        const companiesData = companiesSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().companyName,
        }));
        setCompanies(companiesData);

        const referentsSnapshot = await getDocs(collection(db, "users"));
        const referentsData = referentsSnapshot.docs
          .filter(
            (doc) =>
              doc.data().role === "referent" && doc.data().isActive === true
          )
          .map((doc) => ({
            id: doc.id,
            name: `${doc.data().firstName} ${doc.data().lastName}`,
            firstName: doc.data().firstName,
            companyId: doc.data().companyId,
          }));
        setReferents(referentsData);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };
    fetchData();
  }, []);

  /**
   * Changement du statut du questionnaire personnel
   */
  const handlePartOfCompanyChange = () => {
    setErrors({});
    setIsPartOfCompany(true);
    setIsPersonalQuestionnaire(false);
    setFormData((prevData) => ({
      ...prevData,
      partOfCompany: true,
      personalQuestionnaire: false,
      referentCompanyId:null,
    }));
  };

  /**
   * Changement du statut du questionnaire personnel
   */
  const handlePersonalQuestionnaireChange = () => {
    setErrors({});
    setIsPartOfCompany(false);
    setIsPersonalQuestionnaire(true);
    setFormData((prevData) => ({
      ...prevData,
      partOfCompany: false,
      personalQuestionnaire: true,   
      companyId: prevData?.referentId ? prevData?.referentCompanyId ? prevData?.referentCompanyId : null : null,
    }));
  };
/**
   * Choix de l'entreprise
   * @param {string} value 
   */
  const handleCompanySelection = (value) => {
    const selectedCompany = companies.find((company) => company.name === value);
    setFormData((prevData) => ({
      ...prevData,
      companyName: value,
      companyId: selectedCompany ? selectedCompany.id : "",
    }));
  };

  /**
   * Choix du référent
   */
  const handleReferentSelection = (value) => {
    // Accepter "Pierre naquet" ou "Pierre Naquet" comme valeur valide même si pas dans la liste
    const normalizedValue = value.toLowerCase().trim();
    if (normalizedValue === "pierre naquet" || normalizedValue.includes("pierre naquet")) {
      setFormData((prevData) => ({
        ...prevData,
        referentName: "Pierre Naquet",
        referentId: "HpQzeYpmLKP5TZa0mgH8wEPtu7E2", // ID spécial pour Pierre Naquet
        companyId: "",
        referentCompanyId: "",
      }));
      // Effacer les erreurs si elles existent
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.referentId;
        return newErrors;
      });
      return;
    }

    // Trouver le référent par nom
    const selectedReferent = referents.find((referent) => referent.name === value);
    
    if (!selectedReferent) {
      setErrors((prev) => ({
        ...prev,
        referentId: "Veuillez sélectionner un référent valide ou taper 'Pierre naquet'",
      }));
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      referentName: selectedReferent?.firstName,
      referentId: selectedReferent?.id || "",
      companyId: selectedReferent?.companyId || "",
      referentCompanyId: selectedReferent?.companyId || "",
    }));
    // Effacer les erreurs si elles existent
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.referentId;
      return newErrors;
    });
  };


  return (
    <Form>
      <style>
        {`
          ::placeholder {
            color: #555;  
          }
        `}
      </style>
      <Row className="mb-3 mt-5">
        <Col md={12}>
          <Form.Group controlId="referent">
            <Form.Label style={{ color: "black", fontWeight: "bold" }}>              
              Nom de votre référent coach*
            </Form.Label>          
            {formData.referentName && (
              <div className="d-flex align-items-center mb-2">
                <span style={{ marginRight: '10px' }}>{formData.referentName}</span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      referentName: '',
                      referentId: '',
                      companyId: ''
                    }))
                  }}
                >
                  Retirer le référent
                </Button>
              </div>
            )}
            <Autocomplete
              suggestions={["Pierre Naquet", ...referents.filter(r => r.name.toLowerCase() !== "pierre naquet").map((referent) => referent.name)]}
              placeholder={formData.referentName || "Ex: Jean Dupont"}
              onSelection={handleReferentSelection}
            />
          </Form.Group>
          <div className="text-muted mb-2" style={{ fontSize: '0.9em', marginTop: 8 }}>
              Si vous n'avez pas de référent, tapez "Pierre naquet"
            </div>
          {errors?.referentId && (
            <p style={{ color: "red", fontSize: '0.9em', marginTop: 4 }}>
              {errors.referentId}
            </p>
          )}
        </Col>
      </Row>

      <Row className="mb-3" style={{ marginTop: 56 }}>
        <Col md={12}>
          <Form.Group
            controlId="partOfCompany"
            className="d-flex align-items-center">
            <CustomCheckbox
              label="Je fais partie d'une entreprise"
              id="partOfCompany"
              name="partOfCompany"
              value="partOfCompany"
              checked={isPartOfCompany}
              onChange={handlePartOfCompanyChange}
            />
          </Form.Group>
          <Form.Group
            controlId="personalQuestionnaire"
            className="d-flex align-items-center mt-4">
            <CustomCheckbox
              label="Je passe le questionnaire à titre personnel"
              id="personalQuestionnaire"
              name="personalQuestionnaire"
              value="personalQuestionnaire"
              checked={isPersonalQuestionnaire}
              onChange={handlePersonalQuestionnaireChange}
            />
          </Form.Group>
          {errors?.selection && (
            <div className="alert alert-danger mt-2" style={{ fontSize: '0.9em', padding: '8px 12px' }}>
              {errors.selection}
            </div>
          )}
        </Col>
      </Row>

      {isPartOfCompany && (
        <>
          <Row className="mb-3" style={{ marginTop: 42 }}>
            <Col md={12}>
              <Form.Group controlId="companyName">
                <Form.Label style={{ color: "black", fontWeight: "bold" }}>
                  Tapez le nom de votre entreprise
                </Form.Label>
                <Autocomplete
                  suggestions={companies.map((company) => company.name)}
                  placeholder={"Ex: SNCF"}
                  onSelection={handleCompanySelection}
                />
                {errors?.companyName && (
                  <p style={{ color: "red", fontSize: '0.9em', marginTop: 4 }}>
                    {errors.companyName}
                  </p>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="businessUnit">
                <Form.Label style={{ color: "black", fontWeight: "bold" }}>
                  Business Unit
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Business Unit"
                  name="businessUnit"
                  value={formData.businessUnit}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      businessUnit: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}
    </Form>
  );
};

export default Step2;
